import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { ResultGroupRow } from '../outreachTypes';
import { ratePercent, resultGroupLabel, type ResultGroup } from './resultsCopy';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WorkedGroupProps {
  title: string;
  group: ResultGroup;
  /** The groups of 30 pitches or more; null when no group has 30 ("Too few pitches yet"). */
  rows: ResultGroupRow[] | null;
  /** The number that the bar shows: replies or adds per pitch. */
  metric: 'replied' | 'added';
}

/** One column of "What worked" (design outreach-v2 n1): a rate bar for each group. */
const WorkedGroupComponent: React.FC<WorkedGroupProps> = ({ title, group, rows, metric }) => {
  const intl = useIntl();
  const bars = useMemo(() => {
    if (rows === null) {
      return [];
    }
    const rates = rows.map((row) => ({ row, rate: ratePercent(row[metric], row.pitched) }));
    const top = Math.max(0.1, ...rates.map((item) => item.rate));
    return rates.map(({ row, rate }) => (
      <div key={row.key} className={styles.barRow} data-testid={`worked-${group}-row`}>
        <span>{resultGroupLabel(intl, group, row.key)}</span>
        <div className={styles.bar} aria-hidden>
          <i style={{ width: `${Math.round((rate / top) * 100)}%` }} />
        </div>
        <span className={styles.barValue}>{intl.formatMessage(messages.rate, { percent: rate })}</span>
      </div>
    ));
  }, [group, intl, metric, rows]);
  return (
    <div data-testid={`worked-${group}`}>
      <span className={shared.eyebrow}>{title}</span>
      {rows === null ? (
        <p className={`${shared.small} mt-2 mb-0`} data-testid={`worked-${group}-too-few`}>
          {intl.formatMessage(messages.tooFew)}
        </p>
      ) : (
        <div className={styles.barRows}>{bars}</div>
      )}
    </div>
  );
};

export const WorkedGroup = TypedMemo(WorkedGroupComponent);
